import React, { useEffect, useMemo, useRef, useState } from "react";
import { CustomInput } from "../../../../../../common/styled/common";
import uuid from "react-uuid";
import { MdClose } from "react-icons/md";
import useOutsideClick from "../../../../../../hooks/useOutsideClick";

const CLIPBOARD_BLOCK_PREFIX = "__TODO_BLOCK__";
const TodoScreen = ({ formData, todos, setTodos }) => {
  const memoRef = useRef(null);
  const inputRefs = useRef([]);
  const mberRef = useRef();
  const pointerIdRef = useRef(null);
  const isMouseSelectingRef = useRef(false);
  const dragAnchorIndexRef = useRef(null);
  const dragStartYRef = useRef(0);
  const dragStartedRef = useRef(false);
  const dragStartInTextareaRef = useRef(false);
  const startedInsideTextareaRef = useRef(false);
  const suppressClearSelectionRef = useRef(false);
  const initialCaretOffsetRef = useRef(0);
  const lastPointerEventXRef = useRef(0);
  const charPosCacheRef = useRef({ rowIdx: null, centers: [], rectLeft: 0, rectWidth: 0 });

  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [mentions, setMentions] = useState([]);
  const [matchingMentions, setMatchingMentions] = useState([]);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const [mentionHoverIndex, setMentionHoverIndex] = useState(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [activeMemo, setActiveMemo] = useState(false);
  const [selectionAnchorIdx, setSelectionAnchorIdx] = useState(null);
  const [selectionFocusIdx, setSelectionFocusIdx] = useState(null);
  const [charSelAnchor, setCharSelAnchor] = useState(null);
  const [charSelFocus, setCharSelFocus] = useState(null);
  const [hideNativeSelection, setHideNativeSelection] = useState(false);

  // 되돌리기/다시실행 히스토리
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const isApplyingHistoryRef = useRef(false);
  const lastTodosRef = useRef(JSON.parse(JSON.stringify(todos || [])));

  const deepCopyTodos = (data) => JSON.parse(JSON.stringify(data || []));

  // clientY 추출
  const getEventClientY = (e) => {
    if (typeof e?.clientY === "number") return e.clientY;
    const t = e?.touches?.[0] || e?.changedTouches?.[0];
    return typeof t?.clientY === "number" ? t.clientY : 0;
  };

  // clientX 추출
  const getEventClientX = (e) => {
    if (typeof e?.clientX === "number") return e.clientX;
    const t = e?.touches?.[0] || e?.changedTouches?.[0];
    return typeof t?.clientX === "number" ? t.clientX : 0;
  };

  // 타깃이 textarea이거나 그 내부인지 판별
  const isTargetTextarea = (target) => {
    if (!target) return false;
    if (target.tagName === "TEXTAREA") return true;
    return typeof target.closest === "function" && !!target.closest("textarea");
  };

  // 변경 히스토리 관리 이전 상태 추적
  useEffect(() => {
    if (isApplyingHistoryRef.current) {
      isApplyingHistoryRef.current = false;
      lastTodosRef.current = deepCopyTodos(todos);
      return;
    }
    const prev = lastTodosRef.current;
    if (prev !== todos) {
      historyRef.current.push(prev);
      if (historyRef.current.length > 200) historyRef.current.shift();
      redoRef.current = [];
      lastTodosRef.current = deepCopyTodos(todos);
    }
  }, [todos]);

  // 멘션 팝오버 바깥 클릭 시 닫기
  useOutsideClick(mberRef, () => {
    setIsMentionOpen(false);
    setMentionHoverIndex(null);
  });

  // worker/관리자 목록을 합쳐 중복 제거한 멤버 배열을 생성하고 투두에 반영
  const mergeWorkers = (workerList = {}, manager = null) => {
    const workers = [
      ...Object.entries(workerList || {}).map(([key, value]) => ({
        mberNo: key,
        mberNm: value,
      })),
      manager?.managerNo && { mberNo: manager.managerNo.toString(), mberNm: manager.managerNm },
    ].filter(Boolean);
    const workerIds = workers.map((worker) => worker.mberNo);

    setTodos((data) =>
      [...data].map((todoItem) => ({
        ...todoItem,
        mentionUserIds: todoItem.mentionUserIds.filter((id) => workerIds.includes(id)),
      })),
    );

    return Array.from(new Map(workers.map((worker) => [worker.mberNo, worker])).values());
  };

  // 멤버 목록
  const uniqueWorkers = useMemo(() => {
    return mergeWorkers(formData.workerList, {
      managerNo: formData.managerNo,
      managerNm: formData.managerNm,
    });
  }, [formData.workerList, formData.managerNo, formData.managerNm]);

  // 멤버 목록 변경 시 멘션 리스트 갱신
  useEffect(() => {
    setMentions(uniqueWorkers);
  }, [uniqueWorkers]);

  // 멘션 제안 목록에서 키보드로 위/아래 이동 처리
  const handleMentionNavigation = (key) => {
    if (key === "ArrowUp") {
      setMentionActiveIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : matchingMentions.length - 1,
      );
    } else if (key === "ArrowDown") {
      setMentionActiveIndex((prevIndex) =>
        prevIndex < matchingMentions.length - 1 ? prevIndex + 1 : 0,
      );
    }
  };

  // 블록(여러 행) 선택 상태 여부 계산
  const hasBlockSelection = useMemo(() => {
    return selectionAnchorIdx !== null && selectionFocusIdx !== null;
  }, [selectionAnchorIdx, selectionFocusIdx]);

  const hasCharSelection = useMemo(() => {
    if (!charSelAnchor || !charSelFocus) return false;
    return charSelAnchor.row !== charSelFocus.row || charSelAnchor.offset !== charSelFocus.offset;
  }, [charSelAnchor, charSelFocus]);

  // 선택된 블록의 행 인덱스 목록 반환
  const getSelectedIndices = () => {
    if (!hasBlockSelection) return [];
    const start = Math.min(selectionAnchorIdx, selectionFocusIdx);
    const end = Math.max(selectionAnchorIdx, selectionFocusIdx);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  // 블록 선택 상태 초기화
  const clearBlockSelection = () => {
    setSelectionAnchorIdx(null);
    setSelectionFocusIdx(null);
  };

  // 문자 단위 선택 유틸
  const getCharSelectionRange = () => {
    if (!hasCharSelection) return null;

    const startIsBefore =
      charSelAnchor.row < charSelFocus.row ||
      (charSelAnchor.row === charSelFocus.row && charSelAnchor.offset <= charSelFocus.offset);
    const start = startIsBefore ? charSelAnchor : charSelFocus;
    const end = startIsBefore ? charSelFocus : charSelAnchor;

    return { start, end };
  };

  const clearCharSelection = () => {
    setCharSelAnchor(null);
    setCharSelFocus(null);
  };

  const isRowInCharSelection = (rowIdx) => {
    const range = getCharSelectionRange();
    if (!range) return false;
    return rowIdx >= range.start.row && rowIdx <= range.end.row;
  };

  // 단어 경계 계산 (Ctrl+Shift+Arrow)
  const getPrevWordBoundary = (text, offset) => {
    if (!text) return 0;
    let i = Math.max(0, Math.min(offset, text.length));
    if (i > 0) i--; // 현재 위치가 문자의 끝이라면
    while (i > 0 && /\s/.test(text[i])) i--; // 공백/구분기호
    while (i > 0 && /\w|[가-힣]/.test(text[i - 1])) i--; // 단어 내부

    return i;
  };

  const getNextWordBoundary = (text, offset) => {
    if (!text) return 0;
    let i = Math.max(0, Math.min(offset, text.length));
    while (i < text.length && /\s/.test(text[i])) i++;
    while (i < text.length && /\w|[가-힣]/.test(text[i])) i++;

    return i;
  };

  // 특정 행에서 문자 선택 구간의 시작/끝 offset 계산
  const getRowSelectionOffsets = (rowIdx) => {
    const range = getCharSelectionRange();
    if (!range) return null;
    const text = todos[rowIdx]?.value || "";
    const len = text.length;
    const { start, end } = range;
    if (rowIdx < start.row || rowIdx > end.row) return null;
    const startOffset = rowIdx === start.row ? start.offset : 0;
    const endOffset = rowIdx === end.row ? end.offset : len;
    if (startOffset === endOffset) return null;

    return {
      startOffset: Math.max(0, Math.min(len, startOffset)),
      endOffset: Math.max(0, Math.min(len, endOffset)),
    };
  };

  //  지정 행의 각 문자 중심 X 좌표(행 좌측 기준) 계산 및 캐시 저장
  const prepareCharPositionCache = (rowIdx) => {
    const input = inputRefs.current[rowIdx];
    if (!input) {
      charPosCacheRef.current = { rowIdx: null, centers: [], rectLeft: 0, rectWidth: 0 };
      return;
    }
    const rect = input.getBoundingClientRect();
    const text = todos[rowIdx]?.value || "";
    const codepoints = Array.from(text);
    if (codepoints.length === 0) {
      charPosCacheRef.current = {
        rowIdx,
        centers: [0],
        rectLeft: rect.left,
        rectWidth: rect.width,
      };
      return;
    }

    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "0";
    container.style.top = "0";
    container.style.visibility = "hidden";
    container.style.whiteSpace = "pre-wrap";
    container.style.wordBreak = "break-word";
    container.style.width = `${rect.width}px`;

    const cs = window.getComputedStyle(input);
    container.style.fontFamily = cs.fontFamily;
    container.style.fontSize = cs.fontSize;
    container.style.fontWeight = cs.fontWeight;
    container.style.lineHeight = cs.lineHeight;
    container.style.letterSpacing = cs.letterSpacing;
    container.style.padding = cs.padding;
    container.style.border = cs.border;

    const spans = [];
    for (let i = 0; i < codepoints.length; i++) {
      const span = document.createElement("span");
      span.textContent = codepoints[i];
      container.appendChild(span);
      spans.push(span);
    }

    const tail = document.createElement("span");
    tail.textContent = "";
    container.appendChild(tail);

    document.body.appendChild(container);

    let accumLeft = 0;
    const centers = [];
    for (let i = 0; i < spans.length; i++) {
      const r = spans[i].getBoundingClientRect();
      if (i === 0) accumLeft = r.left; // 현재 줄 기준 좌측
      centers.push(r.left - accumLeft + r.width / 2);
    }

    if (container.parentNode) container.parentNode.removeChild(container);
    charPosCacheRef.current = { rowIdx, centers, rectLeft: rect.left, rectWidth: rect.width };
  };

  // 캐시 이용 빠른 caret offset 계산
  const getCaretOffsetByClientXFast = (rowIdx, clientX) => {
    if (charPosCacheRef.current.rowIdx !== rowIdx) {
      prepareCharPositionCache(rowIdx);
    }
    const { centers, rectLeft } = charPosCacheRef.current;
    if (!centers || centers.length === 0) return 0;
    const targetX = clientX - rectLeft;
    let offset = centers.length; // 기본은 끝
    for (let i = 0; i < centers.length; i++) {
      if (targetX < centers[i]) {
        offset = i;
        break;
      }
    }
    return Math.max(0, Math.min(centers.length, offset));
  };

  // textarea 스타일 읽어 오버레이(미러 텍스트)와 정렬
  const getOverlayInnerStyle = (rowIdx) => {
    const input = inputRefs.current[rowIdx];
    if (!input) return {};
    const cs = window.getComputedStyle(input);
    return {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      fontFamily: cs.fontFamily,
      fontSize: cs.fontSize,
      fontWeight: cs.fontWeight,
      lineHeight: cs.lineHeight,
      letterSpacing: cs.letterSpacing,
      paddingTop: cs.paddingTop,
      paddingBottom: cs.paddingBottom,
      paddingLeft: cs.paddingLeft,
      paddingRight: cs.paddingRight,
      color: "transparent",
    };
  };

  // 빈 공간(행 사이, 위/아래 바깥)에서 가장 가까운 행 인덱스 반환
  const getNearestRowIndexByClientY = (clientY) => {
    const refs = inputRefs.current || [];
    if (refs.length === 0) return null;
    let nearestIndex = 0;
    let minDistance = Infinity;
    for (let i = 0; i < refs.length; i++) {
      const el = refs[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      let distance = 0;
      if (clientY < rect.top) distance = rect.top - clientY;
      else if (clientY > rect.bottom) distance = clientY - rect.bottom;
      else return i; // 내부
      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = i;
      }
    }
    return nearestIndex;
  };

  // 선택된 블록 위/아래로 이동
  const moveSelectedBlock = (direction) => {
    const selected = getSelectedIndices();
    if (selected.length === 0) {
      return { newTodos: todos, newAnchor: selectionAnchorIdx, newFocus: selectionFocusIdx };
    }

    const first = selected[0];
    const last = selected[selected.length - 1];
    const newTodos = [...todos];

    if (direction === "up") {
      if (first === 0)
        return { newTodos, newAnchor: selectionAnchorIdx, newFocus: selectionFocusIdx };
      const block = newTodos.splice(first, selected.length);
      newTodos.splice(first - 1, 0, ...block);
      return { newTodos, newAnchor: selectionAnchorIdx - 1, newFocus: selectionFocusIdx - 1 };
    }
    if (direction === "down") {
      if (last === newTodos.length - 1)
        return { newTodos, newAnchor: selectionAnchorIdx, newFocus: selectionFocusIdx };
      const block = newTodos.splice(first, selected.length);
      newTodos.splice(first + 1, 0, ...block);
      return { newTodos, newAnchor: selectionAnchorIdx + 1, newFocus: selectionFocusIdx + 1 };
    }
    return { newTodos, newAnchor: selectionAnchorIdx, newFocus: selectionFocusIdx };
  };

  // 선택된 투두 내부 전용 구조(JSON)로 직렬화
  const selectedToJSON = () => {
    const indices = getSelectedIndices();
    const data = indices.map((i) => {
      const t = todos[i] || {};
      return {
        value: t.value || "",
        showBullet: !!t.showBullet,
        isChecked: !!t.isChecked,
        mentionUserIds: Array.isArray(t.mentionUserIds) ? t.mentionUserIds : [],
      };
    });
    return JSON.stringify(data);
  };

  // 클립보드 데이터에서 내부 전용 구조 역직렬화
  const parseTodos = (text, e) => {
    try {
      const appData = e?.clipboardData?.getData("application/x-todo-block");
      if (appData) {
        const raw = JSON.parse(appData);
        if (Array.isArray(raw)) {
          return raw.map((item) => ({
            id: uuid(),
            value: item?.value || "",
            showBullet: !!item?.showBullet,
            mentionUserIds: Array.isArray(item?.mentionUserIds) ? [...item.mentionUserIds] : [],
            isChecked: !!item?.isChecked,
          }));
        }
      }
      if (text && text.startsWith(CLIPBOARD_BLOCK_PREFIX)) {
        const raw = JSON.parse(text.slice(CLIPBOARD_BLOCK_PREFIX.length));
        if (Array.isArray(raw)) {
          return raw.map((item) => ({
            id: uuid(),
            value: item?.value || "",
            showBullet: !!item?.showBullet,
            mentionUserIds: Array.isArray(item?.mentionUserIds) ? [...item.mentionUserIds] : [],
            isChecked: !!item?.isChecked,
          }));
        }
      }
    } catch (err) {}
    return null;
  };

  // 선택 블록 복사 (Ctrl/Cmd+C)
  const handleCopy = (e) => {
    // 문자 단위 선택
    if (hasCharSelection) {
      try {
        e.preventDefault();
        const range = getCharSelectionRange();
        if (!range) return;
        const { start, end } = range;
        if (start.row === end.row) {
          const text = (todos[start.row]?.value || "").slice(start.offset, end.offset);
          e.clipboardData.setData("text/plain", text);
        } else {
          const parts = [];
          parts.push((todos[start.row]?.value || "").slice(start.offset));
          for (let i = start.row + 1; i < end.row; i++) parts.push(todos[i]?.value || "");
          parts.push((todos[end.row]?.value || "").slice(0, end.offset));
          e.clipboardData.setData("text/plain", parts.join("\n"));
        }
      } catch (err) {}
      return;
    }
    if (!hasBlockSelection) return;
    try {
      const indices = getSelectedIndices();
      const lines = indices.map((i) => {
        const t = todos[i] || {};
        return `${t.value || ""}`;
      });
      const serialized = selectedToJSON();
      e.preventDefault();
      e.clipboardData.setData("text/plain", lines.join("\n"));
      e.clipboardData.setData("application/x-todo-block", serialized);
    } catch (e) {}
  };

  // 선택 블록 잘라내기 (Ctrl/Cmd+X)
  const handleCut = (e) => {
    if (hasCharSelection) {
      // copy
      handleCopy(e);
      // delete selection
      const range = getCharSelectionRange();
      if (!range) return;
      const { start, end } = range;
      let newTodos = [...todos];
      if (start.row === end.row) {
        const row = { ...newTodos[start.row] };
        const before = row.value.slice(0, start.offset);
        const after = row.value.slice(end.offset);
        row.value = before + after;
        newTodos[start.row] = row;
        setTodos(newTodos);
        clearCharSelection();
        requestAnimationFrame(() => {
          const input = inputRefs.current[start.row];
          if (input) {
            input.focus();
            input.setSelectionRange(before.length, before.length);
          }
        });
      } else {
        const startRow = { ...newTodos[start.row] };
        const endRow = { ...newTodos[end.row] };
        const before = startRow.value.slice(0, start.offset);
        const after = endRow.value.slice(end.offset);
        startRow.value = before + after;
        newTodos[start.row] = startRow;
        newTodos.splice(start.row + 1, end.row - start.row);
        setTodos(newTodos);
        clearCharSelection();
        requestAnimationFrame(() => {
          const input = inputRefs.current[start.row];
          if (input) {
            input.focus();
            input.setSelectionRange(before.length, before.length);
          }
        });
      }
      return;
    }
    if (!hasBlockSelection) return;
    handleCopy(e);
    if (e.defaultPrevented) {
      const selected = getSelectedIndices();
      if (selected.length > 0) {
        let newTodos = [...todos];
        const start = selected[0];
        const count = selected.length;
        newTodos.splice(start, count);
        if (newTodos.length === 0) {
          newTodos = [
            {
              id: uuid(),
              value: "",
              showBullet: false,
              mentionUserIds: [],
              isChecked: false,
            },
          ];
        }
        setTodos(newTodos);
        clearBlockSelection();
        requestAnimationFrame(() => {
          inputRefs.current[Math.min(start, newTodos.length - 1)]?.focus();
        });
      }
    }
  };

  // 멘션 후보 선택 처리
  const handleSelectMention = (mberNo) => {
    const selectedMention =
      typeof mberNo !== "undefined"
        ? matchingMentions.find((mention) => mention.mberNo === mberNo)
        : mentionHoverIndex !== null
        ? matchingMentions[mentionHoverIndex]
        : matchingMentions[mentionActiveIndex];
    if (!selectedMention) return;

    const newTodos = [...todos];
    const index = focusIndex;

    // 투두(불릿)일 때만 멘션 선택 허용
    if (!(index !== null && newTodos[index]?.showBullet)) {
      setIsMentionOpen(false);
      setMatchingMentions([]);
      setMentionHoverIndex(null);
      return;
    }

    const currentValue = newTodos[index].value || "";

    const mentionRegex = /@[\wㄱ-ㅎㅏ-ㅣ가-힣]*$/;
    const match = currentValue.match(mentionRegex);
    if (match) {
      newTodos[index].value = currentValue.slice(0, match.index).trim();
    }

    if (!newTodos[index].mentionUserIds) {
      newTodos[index].mentionUserIds = [];
    }
    if (!newTodos[index].mentionUserIds.includes(selectedMention.mberNo.toString())) {
      newTodos[index].mentionUserIds.push(selectedMention.mberNo.toString());
    }

    setTodos(newTodos);
    setIsMentionOpen(false);
    setMatchingMentions([]);
    setMentionHoverIndex(null);

    requestAnimationFrame(() => {
      const input = inputRefs.current[index];
      if (input) {
        input.focus();
        const caret = newTodos[index].value.length;
        input.setSelectionRange(caret, caret);
      }
    });
  };

  // 출력 가능한 키인지 확인
  const isPrintable = (e) => {
    return !e.ctrlKey && !e.metaKey && !e.altKey && e.key && e.key.length === 1;
  };

  // 멘션 오픈 상태 키 처리 (위/아래/Enter/Escape)
  const mentionKeys = (e) => {
    if (!isMentionOpen) return false;
    if (e.shiftKey) return false;
    if (!(focusIndex !== null && todos[focusIndex]?.showBullet)) return false;
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      setMentionHoverIndex(null);
      handleMentionNavigation(e.key);
      return true;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      handleSelectMention();
      setIsMentionOpen(false);
      setMentionHoverIndex(null);
      return true;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setIsMentionOpen(false);
      setMentionHoverIndex(null);
      return true;
    }
    return false;
  };

  // Shift+위/아래로 블록 선택 이동/시작
  const shiftBlock = (e, index) => {
    if (!(e.shiftKey && (e.key === "ArrowUp" || e.key === "ArrowDown"))) return false;
    e.preventDefault();
    const baseIndex = hasBlockSelection ? selectionFocusIdx : index;
    const nextIndex =
      e.key === "ArrowUp" ? Math.max(0, baseIndex - 1) : Math.min(todos.length - 1, baseIndex + 1);
    if (!hasBlockSelection) {
      setSelectionAnchorIdx(index);
      setSelectionFocusIdx(nextIndex);
    } else {
      setSelectionFocusIdx(nextIndex);
    }
    return true;
  };

  // Escape로 블록 선택 해제
  const escClear = (e) => {
    if (isMentionOpen) return false;
    if (e.key === "Escape" && (hasBlockSelection || hasCharSelection)) {
      e.preventDefault();
      clearBlockSelection();
      clearCharSelection();
      return true;
    }
    return false;
  };

  // Ctrl/Cmd+A로 전체 행 선택
  const selectAll = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return false;
    if (!(e.key === "a" || e.key === "A")) return false;
    e.preventDefault();
    setSelectionAnchorIdx(0);
    setSelectionFocusIdx(todos.length - 1);
    return true;
  };

  // 입력/Enter로 선택 영역 대체
  const replaceBlock = (e) => {
    if (!hasBlockSelection) return false;
    const printable = isPrintable(e);
    const isComposing = e.isComposing || e.key === "Process" || e.keyCode === 229;
    if (!isComposing && (printable || (e.key === "Enter" && !e.shiftKey))) {
      e.preventDefault();
      const selected = getSelectedIndices();
      if (selected.length > 0) {
        const startIdx = selected[0];
        const replacement = {
          id: uuid(),
          value: printable ? e.key : "",
          showBullet: false,
          mentionUserIds: [],
          isChecked: false,
        };
        const newTodos = [...todos];
        newTodos.splice(startIdx, selected.length, replacement);
        setTodos(newTodos);
        clearBlockSelection();
        requestAnimationFrame(() => {
          const input = inputRefs.current[startIdx];
          if (input) {
            input.focus();
            const caret = replacement.value.length;
            input.setSelectionRange(caret, caret);
          }
        });
      }
      return true;
    }
    return false;
  };

  // 블록 선택 삭제 (Backspace/Delete)
  const deleteBlock = (e) => {
    if (!hasBlockSelection) return false;
    if (!(e.key === "Backspace" || e.key === "Delete")) return false;
    e.preventDefault();
    const selected = getSelectedIndices();
    if (selected.length > 0) {
      let newTodos = [...todos];
      const start = selected[0];
      const count = selected.length;
      newTodos.splice(start, count);
      if (newTodos.length === 0) {
        newTodos = [
          {
            id: uuid(),
            value: "",
            showBullet: false,
            mentionUserIds: [],
            isChecked: false,
          },
        ];
      }
      setTodos(newTodos);
      clearBlockSelection();
      const newFocus = Math.min(start, newTodos.length - 1);
      requestAnimationFrame(() => {
        inputRefs.current[newFocus]?.focus();
      });
    }
    return true;
  };

  // Backspace로 행 시작에서 불릿 해제
  const backspaceUnbullet = (e, index) => {
    if (e.key !== "Backspace") return false;
    if (!(e.target.selectionStart === 0 && todos[index].showBullet)) return false;
    const newTodos = [...todos];
    newTodos[index].showBullet = false;
    // 불릿 해제 시 멘션/들여쓰기 초기화
    newTodos[index].mentionUserIds = [];
    setTodos(newTodos);
    return true;
  };

  // Backspace로 행 시작에서 이전 행과 병합
  const backspaceMergePrev = (e, index) => {
    if (e.key !== "Backspace") return false;
    if (!(e.target.selectionStart === 0 && index > 0)) return false;
    e.preventDefault();
    const newTodos = [...todos];
    const prevValue = newTodos[index - 1].value;
    const currentValue = newTodos[index].value;
    newTodos[index - 1].value = prevValue + currentValue;
    newTodos.splice(index, 1);
    setTodos(newTodos);
    requestAnimationFrame(() => {
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
        prevInput.setSelectionRange(prevValue.length, prevValue.length);
      }
    });
    return true;
  };

  // Alt+위/아래로 블록 이동 또는 현재 행 스왑
  const altMove = (e, index) => {
    if (!e.altKey) return false;
    if (!(e.key === "ArrowUp" || e.key === "ArrowDown")) return false;
    e.preventDefault();
    if (hasBlockSelection) {
      const { newTodos, newAnchor, newFocus } = moveSelectedBlock(
        e.key === "ArrowUp" ? "up" : "down",
      );
      setTodos(newTodos);
      setSelectionAnchorIdx(newAnchor);
      setSelectionFocusIdx(newFocus);
      requestAnimationFrame(() => {
        inputRefs.current[Math.min(newAnchor, newFocus)]?.focus();
      });
      return true;
    }
    if (e.key === "ArrowUp" && index > 0) {
      const newTodos = [...todos];
      const temp = newTodos[index - 1];
      newTodos[index - 1] = newTodos[index];
      newTodos[index] = temp;
      setTodos(newTodos);
      requestAnimationFrame(() => {
        const input = inputRefs.current[index - 1];
        if (input) {
          input.focus();
          const caret = e.target.selectionStart;
          const len = newTodos[index - 1].value.length;
          input.setSelectionRange(Math.min(caret, len), Math.min(caret, len));
        }
      });
      return true;
    }
    if (e.key === "ArrowDown" && index < todos.length - 1) {
      const newTodos = [...todos];
      const temp = newTodos[index + 1];
      newTodos[index + 1] = newTodos[index];
      newTodos[index] = temp;
      setTodos(newTodos);
      requestAnimationFrame(() => {
        const input = inputRefs.current[index + 1];
        if (input) {
          input.focus();
          const caret = e.target.selectionStart;
          const len = newTodos[index + 1].value.length;
          input.setSelectionRange(Math.min(caret, len), Math.min(caret, len));
        }
      });
      return true;
    }
    return true;
  };

  // Delete로 행 끝에서 다음 행과 병합 (현재 행과 다음 행 병합)
  const delMergeNext = (e, index) => {
    if (e.key !== "Delete") return false;
    const atLineEnd = e.target.selectionStart === todos[index].value.length;
    if (!(atLineEnd && index < todos.length - 1)) return false;
    e.preventDefault();
    const newTodos = [...todos];
    const currentValue = newTodos[index].value;
    const nextValue = newTodos[index + 1].value;
    newTodos[index].value = currentValue + nextValue;
    newTodos.splice(index + 1, 1);
    setTodos(newTodos);
    requestAnimationFrame(() => {
      const currentInput = inputRefs.current[index];
      if (currentInput) {
        currentInput.focus();
        currentInput.setSelectionRange(currentValue.length, currentValue.length);
      }
    });
    return true;
  };

  // 화살표 이동 처리
  const arrows = (e, index) => {
    if (e.key === "ArrowUp") moveFocus("up", index, e.target.selectionStart);
    if (e.key === "ArrowDown") moveFocus("down", index, e.target.selectionStart);
    if (e.key === "ArrowLeft") moveFocus("left", index, e.target.selectionStart);
    if (e.key === "ArrowRight")
      moveFocus("right", index, e.target.selectionStart, todos[index].value.length);
  };

  // 붙여넣기/변경 유틸리티
  const getCursor = (e) => e.target.selectionStart;
  const parsePaste = (text, e) => parseTodos(text, e);
  const pasteReplace = (baseTodos, insertAt, selectedCount, structured) => {
    const newTodos = [...baseTodos];
    newTodos.splice(insertAt, selectedCount, ...structured);
    return newTodos;
  };
  const pasteInlineSplit = (baseTodos, index, cursorPosition, structured) => {
    const newTodos = [...baseTodos];
    const current = { ...newTodos[index] };
    const before = current.value.slice(0, cursorPosition);
    const after = current.value.slice(cursorPosition);
    current.value = before;
    newTodos[index] = current;
    newTodos.splice(index + 1, 0, ...structured);
    if (after && after.length > 0) {
      newTodos.splice(index + 1 + structured.length, 0, {
        id: uuid(),
        value: after,
        showBullet: current.showBullet,
        mentionUserIds: [],
        isChecked: false,
      });
    }
    return newTodos;
  };

  // @ 키로 멘션 시작
  const handleAtKeyMentionStart = (e, index) => {
    if (!(e.key === "@" && !!todos[index].showBullet)) return false;
    if (hasBlockSelection) {
      const selected = getSelectedIndices();
      if (selected.length > 0) {
        e.preventDefault();
        const startIdx = selected[0];
        const newTodos = [...todos];
        newTodos.splice(startIdx, selected.length, {
          id: uuid(),
          value: "",
          showBullet: true,
          mentionUserIds: [],
          isChecked: false,
        });
        setTodos(newTodos);
        clearBlockSelection();
        requestAnimationFrame(() => {
          const input = inputRefs.current[startIdx];
          if (input) input.focus();
        });

        index = startIdx;
      }
    }
    setIsMentionOpen(true);
    setMatchingMentions(mentions);
    setMentionActiveIndex(0);
    setMentionHoverIndex(null);
    return true;
  };

  // 입력 처리
  const handleKeyDown = (e, index) => {
    setFocusIndex(index);

    // 되돌리기/다시실행
    if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z") && !e.shiftKey) {
      e.preventDefault();
      const prev = historyRef.current.pop();
      if (prev) {
        const currentCopy = deepCopyTodos(todos);
        redoRef.current.push(currentCopy);
        isApplyingHistoryRef.current = true;
        setTodos(prev);
      }
      return;
    }
    if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "y" || (e.shiftKey && (e.key === "Z" || e.key === "z")))
    ) {
      e.preventDefault();
      const next = redoRef.current.pop();
      if (next) {
        const currentCopy = deepCopyTodos(todos);
        historyRef.current.push(currentCopy);
        isApplyingHistoryRef.current = true;
        setTodos(next);
      }
      return;
    }

    // Shift+화살표/단어 단위/행 단위 확장
    const isArrowKey = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key);
    if (e.shiftKey && (isArrowKey || e.key === "Home" || e.key === "End")) {
      e.preventDefault();
      const caret = typeof e.target.selectionStart === "number" ? e.target.selectionStart : 0;
      const anchor = charSelAnchor || { row: index, offset: caret };
      let focus = charSelFocus || { row: index, offset: caret };
      const currentText = todos[focus.row]?.value || "";
      if (e.key === "ArrowLeft") {
        if (e.ctrlKey || e.metaKey) {
          const boundary = getPrevWordBoundary(currentText, focus.offset);
          focus = { row: focus.row, offset: boundary };
        } else if (focus.offset > 0) {
          focus = { row: focus.row, offset: focus.offset - 1 };
        } else if (focus.row > 0) {
          const prevLen = todos[focus.row - 1]?.value?.length || 0;
          focus = { row: focus.row - 1, offset: prevLen };
        }
      } else if (e.key === "ArrowRight") {
        const len = currentText.length;
        if (e.ctrlKey || e.metaKey) {
          const boundary = getNextWordBoundary(currentText, focus.offset);
          focus = { row: focus.row, offset: boundary };
        } else if (focus.offset < len) {
          focus = { row: focus.row, offset: focus.offset + 1 };
        } else if (focus.row < todos.length - 1) {
          focus = { row: focus.row + 1, offset: 0 };
        }
      } else if (e.key === "ArrowUp") {
        if (focus.row > 0) {
          const prevLen = todos[focus.row - 1]?.value?.length || 0;
          const desired = Math.min(focus.offset, prevLen);
          focus = { row: focus.row - 1, offset: desired };
        }
      } else if (e.key === "ArrowDown") {
        if (focus.row < todos.length - 1) {
          const nextLen = todos[focus.row + 1]?.value?.length || 0;
          const desired = Math.min(focus.offset, nextLen);
          focus = { row: focus.row + 1, offset: desired };
        }
      } else if (e.key === "Home") {
        focus = { row: focus.row, offset: 0 };
      } else if (e.key === "End") {
        const len = currentText.length;
        focus = { row: focus.row, offset: len };
      }
      setCharSelAnchor(anchor);
      setCharSelFocus(focus);
      // 다른 줄로 포커스를 옮길 때
      suppressClearSelectionRef.current = true;
      requestAnimationFrame(() => {
        const input = inputRefs.current[focus.row];
        if (input) {
          input.focus();
          input.setSelectionRange(focus.offset, focus.offset);
        }
        setTimeout(() => {
          suppressClearSelectionRef.current = false;
        }, 0);
      });
      return;
    }

    // 문자 단위 선택: 입력/Enter/삭제 처리
    const printable = isPrintable(e);
    const isComposing = e.isComposing || e.key === "Process" || e.keyCode === 229;
    if (hasCharSelection && !isComposing) {
      if (printable || (e.key === "Enter" && !e.shiftKey)) {
        e.preventDefault();
        const range = getCharSelectionRange();
        if (range) {
          const { start, end } = range;
          let newTodos = [...todos];
          if (start.row === end.row) {
            const row = { ...newTodos[start.row] };
            const before = row.value.slice(0, start.offset);
            const after = row.value.slice(end.offset);
            if (printable) {
              row.value = before + e.key + after;
              newTodos[start.row] = row;
              setTodos(newTodos);
              clearCharSelection();
              requestAnimationFrame(() => {
                const input = inputRefs.current[start.row];
                if (input) {
                  const pos = before.length + e.key.length;
                  input.focus();
                  input.setSelectionRange(pos, pos);
                }
              });
              return;
            }
            // Enter 같은 줄에서 분리
            const currentTodo = { ...newTodos[start.row] };
            const beforeCursor = before;
            const afterCursor = after;
            newTodos[start.row] = { ...currentTodo, value: beforeCursor };
            newTodos.splice(start.row + 1, 0, {
              id: uuid(),
              value: afterCursor,
              showBullet: currentTodo.showBullet,
              mentionUserIds: [],
            });
            setTodos(newTodos);
            clearCharSelection();
            requestAnimationFrame(() => {
              inputRefs.current[start.row + 1]?.focus();
            });
            return;
          }
          // 여러 줄에 걸친 선택
          const startRow = { ...newTodos[start.row] };
          const endRow = { ...newTodos[end.row] };
          const before = startRow.value.slice(0, start.offset);
          const after = endRow.value.slice(end.offset);
          startRow.value = before + after;
          newTodos[start.row] = startRow;
          newTodos.splice(start.row + 1, end.row - start.row);
          if (printable) {
            const row = { ...newTodos[start.row] };
            const pos = before.length + e.key.length;
            row.value = before + e.key + after;
            newTodos[start.row] = row;
            setTodos(newTodos);
            clearCharSelection();
            requestAnimationFrame(() => {
              const input = inputRefs.current[start.row];
              if (input) {
                input.focus();
                input.setSelectionRange(pos, pos);
              }
            });
            return;
          }
          // Enter 줄 분리
          const currentTodo = { ...newTodos[start.row] };
          const beforeCursor = before;
          const afterCursor = after;
          newTodos[start.row] = { ...currentTodo, value: beforeCursor };
          newTodos.splice(start.row + 1, 0, {
            id: uuid(),
            value: afterCursor,
            showBullet: currentTodo.showBullet,
            mentionUserIds: [],
          });
          setTodos(newTodos);
          clearCharSelection();
          requestAnimationFrame(() => {
            inputRefs.current[start.row + 1]?.focus();
          });
          return;
        }
      }
      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        const range = getCharSelectionRange();
        if (!range) return;
        const { start, end } = range;
        let newTodos = [...todos];
        if (start.row === end.row) {
          const row = { ...newTodos[start.row] };
          const before = row.value.slice(0, start.offset);
          const after = row.value.slice(end.offset);
          row.value = before + after;
          newTodos[start.row] = row;
          setTodos(newTodos);
          clearCharSelection();
          requestAnimationFrame(() => {
            const input = inputRefs.current[start.row];
            if (input) {
              input.focus();
              input.setSelectionRange(before.length, before.length);
            }
          });
          return;
        }
        const startRow = { ...newTodos[start.row] };
        const endRow = { ...newTodos[end.row] };
        const before = startRow.value.slice(0, start.offset);
        const after = endRow.value.slice(end.offset);
        startRow.value = before + after;
        newTodos[start.row] = startRow;
        newTodos.splice(start.row + 1, end.row - start.row);
        setTodos(newTodos);
        clearCharSelection();
        requestAnimationFrame(() => {
          const input = inputRefs.current[start.row];
          if (input) {
            input.focus();
            input.setSelectionRange(before.length, before.length);
          }
        });
        return;
      }
    }

    if (mentionKeys(e)) return;
    if (shiftBlock(e, index)) return;
    if (escClear(e)) return;
    if (selectAll(e)) return;
    if (replaceBlock(e)) return;
    if (e.key === "Enter") {
      handleEnter(e, index);
      return;
    }
    if (deleteBlock(e)) return;
    if (backspaceUnbullet(e, index)) return;
    if (backspaceMergePrev(e, index)) return;
    if (altMove(e, index)) return;
    if (delMergeNext(e, index)) return;
    arrows(e, index);
    if (handleAtKeyMentionStart(e, index)) return;
  };

  // 텍스트 변경 및 멘션 리스트 필터링
  const isOrderedPrefix = (text) => /^\d+\.\s?$/.test(text);
  const stripOrderedPrefix = (text) => text.replace(/^\d+\.\s?/, "");

  const buildChoseong = (fullName) =>
    fullName
      .toLowerCase()
      .split("")
      .map((char) => char.charCodeAt(0) - 44032)
      .filter((code) => code >= 0 && code <= 11172)
      .map((code) => String.fromCharCode(0x1100 + Math.floor(code / 588)))
      .join("");
  const filterMentions = (mentionsList, query, existingIds) =>
    mentionsList.filter((m) => {
      const fullName = m.mberNm.toLowerCase();
      const initials = buildChoseong(fullName);
      return (
        (fullName.startsWith(query) || initials.startsWith(query)) &&
        !existingIds.includes(m.mberNo)
      );
    });

  const handleChange = (e, index) => {
    const newTodos = [...todos];
    let text = e.target.value;
    let adjustCaretTo = null;

    // 행 맨 앞에서 숫자. + 스페이스가 입력된 경우 불릿 전환
    if (!newTodos[index].showBullet) {
      const cursorPosition = e.target.selectionStart;
      const prefixMatch = text.match(/^(\d+)\.\s/);
      if (prefixMatch && cursorPosition === prefixMatch[0].length) {
        newTodos[index].showBullet = true;
        text = text.slice(prefixMatch[0].length);
        adjustCaretTo = 0;
      } else if (isOrderedPrefix(text)) {
        newTodos[index].showBullet = true;
        text = stripOrderedPrefix(text);
        adjustCaretTo = 0;
      }
    }

    newTodos[index].value = text;
    setTodos(newTodos);

    if (adjustCaretTo !== null) {
      requestAnimationFrame(() => {
        const input = inputRefs.current[index];
        if (input) {
          input.focus();
          input.setSelectionRange(adjustCaretTo, adjustCaretTo);
        }
      });
    }

    const cursorPosition = e.target.selectionStart;
    const mentionQuery = text.slice(0, cursorPosition).match(/@([^\s@!#$%^&*(),.?":{}|<>]*)$/);
    if (mentionQuery && newTodos[index].showBullet) {
      const query = mentionQuery[1].toLowerCase();
      const existingMentions = (newTodos[index].mentionUserIds || []).map((mention) => mention);
      const filteredMentions = filterMentions(mentions, query, existingMentions);
      setMatchingMentions(filteredMentions);
      setIsMentionOpen(true);
      setMentionActiveIndex(0);
      setMentionHoverIndex(null);
    } else {
      setIsMentionOpen(false);
      setMatchingMentions([]);
      setMentionHoverIndex(null);
    }
  };

  // 드래그로 블록 선택
  const handleRowMouseDown = (index, e) => {
    if (e.target.closest("button")) {
      return;
    }

    clearCharSelection();
    const isMouse = e && (e.pointerType === "mouse" || typeof e.button === "number");
    if (isMouse && e.button !== 0) return;

    isMouseSelectingRef.current = true;
    suppressClearSelectionRef.current = true;
    dragAnchorIndexRef.current = index;
    dragStartYRef.current = getEventClientY(e);
    dragStartedRef.current = false;

    const isInTextarea = isTargetTextarea(e.target);
    setHideNativeSelection(!isInTextarea);
    dragStartInTextareaRef.current = !!isInTextarea;
    startedInsideTextareaRef.current = !!isInTextarea;

    if (
      !isInTextarea &&
      memoRef.current &&
      typeof e?.pointerId === "number" &&
      typeof memoRef.current.setPointerCapture === "function"
    ) {
      try {
        memoRef.current.setPointerCapture(e.pointerId);
        pointerIdRef.current = e.pointerId;
      } catch (_) {}
    }

    if (isInTextarea) {
      const input = inputRefs.current[index];
      const caret = input ? input.selectionStart ?? 0 : 0;
      initialCaretOffsetRef.current = caret;
      setCharSelAnchor({ row: index, offset: caret });
      setCharSelFocus({ row: index, offset: caret });
    } else {
      // 행 전체 기준으로 시작
      initialCaretOffsetRef.current = 0;
      setCharSelAnchor({ row: index, offset: 0 });
      setCharSelFocus({ row: index, offset: 0 });
    }
  };

  // 드래그 중 블록 선택 범위 업데이트
  const handleRowMouseEnter = (index, e) => {
    // 버튼 클릭인 경우 처리하지 않음
    if (e.target.closest("button")) {
      return;
    }

    if (!isMouseSelectingRef.current) return;
    if (!dragStartedRef.current) {
      const anchor = dragAnchorIndexRef.current;
      if (dragStartInTextareaRef.current && index !== anchor) {
        dragStartedRef.current = true;
        // 다른 줄 진입 시 포인터 X 기준 caret 계산
        clearBlockSelection();
        const x = getEventClientX(e) || lastPointerEventXRef.current || 0;
        lastPointerEventXRef.current = x;
        // 줄 간 문자 선택 시작 시 캐시 준비 및 포인터 캡처
        prepareCharPositionCache(index);
        if (
          memoRef.current &&
          typeof e?.pointerId === "number" &&
          typeof memoRef.current.setPointerCapture === "function"
        ) {
          try {
            memoRef.current.setPointerCapture(e.pointerId);
            pointerIdRef.current = e.pointerId;
          } catch (_) {}
        }
        const offset = getCaretOffsetByClientXFast(index, x);
        setCharSelFocus({ row: index, offset });
        // 줄 간 문자 선택 시작 네이티브 배경 숨김
        setHideNativeSelection(true);
      }
      return;
    }
    if (dragStartInTextareaRef.current) {
      const anchor = dragAnchorIndexRef.current;
      if (index === anchor) return;
      const x = getEventClientX(e) || lastPointerEventXRef.current || 0;
      lastPointerEventXRef.current = x;
      if (charPosCacheRef.current.rowIdx !== index) prepareCharPositionCache(index);
      const offset = getCaretOffsetByClientXFast(index, x);
      setCharSelFocus({ row: index, offset });
      return;
    }
    setSelectionFocusIdx(index);
  };

  // 드래그 종료
  const handleMouseUpAnywhere = () => {
    isMouseSelectingRef.current = false;
    dragStartedRef.current = false;
    dragStartInTextareaRef.current = false;
    suppressClearSelectionRef.current = false;
    setHideNativeSelection(false);
    // 캐시 정리
    charPosCacheRef.current = { rowIdx: null, centers: [], rectLeft: 0, rectWidth: 0 };
    if (
      memoRef.current &&
      pointerIdRef.current !== null &&
      typeof memoRef.current.releasePointerCapture === "function"
    ) {
      try {
        memoRef.current.releasePointerCapture(pointerIdRef.current);
      } catch (_) {}
      pointerIdRef.current = null;
    }
  };

  // 드래그 중 블록 선택 범위 업데이트
  const handleMouseMove = (e) => {
    if (!isMouseSelectingRef.current) return;
    const clientY = getEventClientY(e);
    const cx = getEventClientX(e);
    if (typeof cx === "number" && !Number.isNaN(cx)) lastPointerEventXRef.current = cx;
    const distance = Math.abs(clientY - dragStartYRef.current);
    if (!dragStartedRef.current && distance >= 3) {
      dragStartedRef.current = true;
      if (!dragStartInTextareaRef.current) {
        const anchor = dragAnchorIndexRef.current;
        setSelectionAnchorIdx(anchor);
        setSelectionFocusIdx(anchor);
      }
    }
    if (dragStartedRef.current) {
      // 문자 선택 확장
      if (dragStartInTextareaRef.current) {
        const idx = getNearestRowIndexByClientY(getEventClientY(e));
        if (idx !== null) {
          const x = lastPointerEventXRef.current || getEventClientX(e) || 0;
          if (charPosCacheRef.current.rowIdx !== idx) prepareCharPositionCache(idx);
          const offset = getCaretOffsetByClientXFast(idx, x);
          setCharSelFocus({ row: idx, offset });
          // 줄 간 문자 선택 유지
          setHideNativeSelection(true);
        }
        // 같은 줄 내부 드래그는 네이티브 selection 허용
        const anchor = dragAnchorIndexRef.current;
        if (idx !== null && idx !== anchor) e.preventDefault();
        return;
      }
      const idx = getNearestRowIndexByClientY(getEventClientY(e));
      if (idx !== null) {
        setSelectionFocusIdx(idx);
      }
      e.preventDefault();
    }
  };

  // 한글 조합 시작 시 블록 대체
  const startComposition = () => {
    if (hasCharSelection) {
      const range = getCharSelectionRange();
      if (!range) return false;
      const { start, end } = range;
      const newTodos = [...todos];
      if (start.row === end.row) {
        const row = { ...newTodos[start.row] };
        const before = row.value.slice(0, start.offset);
        const after = row.value.slice(end.offset);
        row.value = before + after;
        newTodos[start.row] = row;
        setTodos(newTodos);
        clearCharSelection();
        requestAnimationFrame(() => {
          const input = inputRefs.current[start.row];
          if (input) {
            input.focus();
            input.setSelectionRange(before.length, before.length);
          }
        });
        return true;
      }
      const startRow = { ...newTodos[start.row] };
      const endRow = { ...newTodos[end.row] };
      const before = startRow.value.slice(0, start.offset);
      const after = endRow.value.slice(end.offset);
      startRow.value = before + after;
      newTodos[start.row] = startRow;
      newTodos.splice(start.row + 1, end.row - start.row);
      setTodos(newTodos);
      clearCharSelection();
      requestAnimationFrame(() => {
        const input = inputRefs.current[start.row];
        if (input) {
          input.focus();
          input.setSelectionRange(before.length, before.length);
        }
      });
      return true;
    }
    if (!hasBlockSelection) return false;
    const selected = getSelectedIndices();
    if (selected.length === 0) return false;
    const startIdx = selected[0];
    const replacement = {
      id: uuid(),
      value: "",
      showBullet: false,
      mentionUserIds: [],
      isChecked: false,
    };
    const newTodos = [...todos];
    newTodos.splice(startIdx, selected.length, replacement);
    setTodos(newTodos);
    clearBlockSelection();
    requestAnimationFrame(() => {
      const input = inputRefs.current[startIdx];
      if (input) {
        input.focus();
        input.setSelectionRange(0, 0);
      }
    });
    return true;
  };

  // Enter 처리 (행 분할/모드 전환)
  const handleEnter = (e, index) => {
    e.preventDefault();

    const currentTodo = todos[index];
    const cursorPosition = e.target.selectionStart;
    const beforeCursor = currentTodo.value.slice(0, cursorPosition);
    const afterCursor = currentTodo.value.slice(cursorPosition);

    if (!currentTodo.showBullet) {
      const newTodos = [...todos];
      newTodos[index].value = beforeCursor;

      newTodos.splice(index + 1, 0, {
        id: uuid(),
        value: afterCursor,
        showBullet: false,
        mentionUserIds: [],
      });

      setTodos(newTodos);

      requestAnimationFrame(() => {
        inputRefs.current[index + 1]?.focus();
      });

      return;
    }

    if (currentTodo.value === "" && currentTodo.showBullet) {
      const newTodos = [...todos];
      newTodos[index].showBullet = false;
      newTodos[index].mentionUserIds = [];
      setTodos(newTodos);

      requestAnimationFrame(() => {
        inputRefs.current[index + 1]?.focus();
      });

      return;
    }

    const newTodos = [...todos];
    newTodos[index].value = beforeCursor;

    newTodos.splice(index + 1, 0, {
      id: uuid(),
      value: afterCursor,
      showBullet: currentTodo.showBullet,
      mentionUserIds: [],
    });

    setTodos(newTodos);

    requestAnimationFrame(() => {
      inputRefs.current[index + 1]?.focus();
    });
  };

  // 방향키로 행 간 커서 이동
  const moveFocus = (direction, index, cursorPos = 0, textLength = 0) => {
    if (direction === "up" && index > 0) {
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        const newCursorPos = Math.min(cursorPos, prevInput.value.length);
        prevInput.focus();
        prevInput.setSelectionRange(newCursorPos, newCursorPos);
      }
    } else if (direction === "down" && index < todos.length - 1) {
      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        const newCursorPos = Math.min(cursorPos, nextInput.value.length);
        nextInput.focus();
        nextInput.setSelectionRange(newCursorPos, newCursorPos);
      }
    } else if (direction === "left") {
      if (cursorPos === 0 && index > 0) {
        const prevLength = todos[index - 1].value.length;
        requestAnimationFrame(() => {
          inputRefs.current[index - 1]?.focus();
          inputRefs.current[index - 1]?.setSelectionRange(prevLength, prevLength);
        });
      }
    } else if (direction === "right") {
      if (cursorPos === textLength && index < todos.length - 1) {
        requestAnimationFrame(() => {
          inputRefs.current[index + 1]?.focus();
          inputRefs.current[index + 1]?.setSelectionRange(0, 0);
        });
      }
    }
    setIsMentionOpen(false);
  };

  // 붙여넣기
  const isNumberedLine = (line) => /^\d+\.\s*/.test(line);

  const cleanAndSplitLines = (text) => text.replace(/\r/g, "").split("\n");

  const applySingleLinePaste = (currentTodo, firstLine, beforeCursor, afterCursor) => {
    const mergedValue = beforeCursor + firstLine + afterCursor;
    const numbered = isNumberedLine(firstLine) && beforeCursor.trim() === "";
    const newValue = numbered
      ? beforeCursor + firstLine.replace(/^\d+\.\s*/, "") + afterCursor
      : mergedValue;
    return { value: newValue, showBullet: currentTodo.showBullet || numbered };
  };

  const applyFirstLineOfMulti = (currentShowBullet, firstLine, beforeCursor) => {
    const numbered = isNumberedLine(firstLine) && beforeCursor.trim() === "";
    const newValue = numbered
      ? beforeCursor + firstLine.replace(/^\d+\.\s*/, "")
      : beforeCursor + firstLine;
    return { value: newValue, showBullet: currentShowBullet || numbered };
  };

  const insertIntermediateLines = (newTodos, baseIndex, lines) => {
    lines.forEach((line, i) => {
      const numbered = isNumberedLine(line);
      newTodos.splice(baseIndex + i + 1, 0, {
        id: uuid(),
        value: numbered ? line.replace(/^\d+\.\s*/, "") : line,
        showBullet: numbered,
        mentionUserIds: [],
      });
    });
  };

  const applyLastLineMergeOrInsert = (newTodos, nextIndex, lastLine, afterCursor) => {
    const numbered = isNumberedLine(lastLine);
    if (nextIndex < newTodos.length) {
      if (!afterCursor || afterCursor.length === 0) {
        // 다음 행을 덮지 않고 새 행 삽입
        newTodos.splice(nextIndex, 0, {
          id: uuid(),
          value: numbered ? lastLine.replace(/^\d+\.\s*/, "") : lastLine,
          showBullet: numbered,
          mentionUserIds: [],
        });
      } else {
        const nextTodo = newTodos[nextIndex];
        nextTodo.value =
          (numbered ? lastLine.replace(/^\d+\.\s*/, "") : lastLine) + afterCursor + nextTodo.value;
        nextTodo.showBullet = nextTodo.showBullet || numbered;
        newTodos[nextIndex] = { ...nextTodo };
      }
    } else {
      newTodos.splice(nextIndex, 0, {
        id: uuid(),
        value: numbered ? lastLine.replace(/^\d+\.\s*/, "") + afterCursor : lastLine + afterCursor,
        showBullet: numbered,
        mentionUserIds: [],
      });
    }
  };

  // 붙여넣기 이벤트 처리
  const checkPastedBoard = (text, index, cursorPosition, baseTodos = todos) => {
    const lines = cleanAndSplitLines(text);
    const newTodos = [...baseTodos];

    if (lines.length === 0) {
      return newTodos;
    }

    const firstLine = lines[0];
    const currentTodo = newTodos[index];
    const beforeCursor = currentTodo.value.slice(0, cursorPosition);
    const afterCursor = currentTodo.value.slice(cursorPosition);

    if (lines.length === 1) {
      const { value, showBullet } = applySingleLinePaste(
        currentTodo,
        firstLine,
        beforeCursor,
        afterCursor,
      );
      newTodos[index] = { ...currentTodo, value, showBullet };
      return newTodos;
    }

    const { value: firstValue, showBullet: firstShowBullet } = applyFirstLineOfMulti(
      currentTodo.showBullet,
      firstLine,
      beforeCursor,
    );
    newTodos[index] = { ...currentTodo, value: firstValue, showBullet: firstShowBullet };

    // 중간 라인 삽입
    insertIntermediateLines(newTodos, index, lines.slice(1, -1));

    // 마지막 라인 처리 (다음 행 병합 또는 신규 삽입)
    const lastLine = lines[lines.length - 1];
    const nextIndex = index + lines.length - 1;
    applyLastLineMergeOrInsert(newTodos, nextIndex, lastLine, afterCursor);

    return newTodos;
  };

  // 붙여넣기 처리 (Ctrl/Cmd+V)
  const handlePaste = (e, index) => {
    if (hasCharSelection) {
      e.preventDefault();
      const text = e.clipboardData.getData("text");
      const range = getCharSelectionRange();
      if (!range) return;
      const { start, end } = range;
      let prepared = [...todos];
      if (start.row === end.row) {
        const row = { ...prepared[start.row] };
        const before = row.value.slice(0, start.offset);
        const after = row.value.slice(end.offset);
        row.value = before + after;
        prepared[start.row] = row;
        const result = checkPastedBoard(text, start.row, before.length, prepared);
        setTodos(result);
        clearCharSelection();
        requestAnimationFrame(() => {
          inputRefs.current[start.row]?.focus();
        });
      } else {
        const startRow = { ...prepared[start.row] };
        const endRow = { ...prepared[end.row] };
        const before = startRow.value.slice(0, start.offset);
        const after = endRow.value.slice(end.offset);
        startRow.value = before + after;
        prepared[start.row] = startRow;
        prepared.splice(start.row + 1, end.row - start.row);
        const result = checkPastedBoard(text, start.row, before.length, prepared);
        setTodos(result);
        clearCharSelection();
        requestAnimationFrame(() => {
          inputRefs.current[start.row]?.focus();
        });
      }
      return;
    }
    const text = e.clipboardData.getData("text");
    const structured = parsePaste(text, e);
    if (structured && structured.length > 0) {
      e.preventDefault();
      const insertAt = hasBlockSelection ? getSelectedIndices()[0] ?? index : index;
      const newTodos = hasBlockSelection
        ? pasteReplace(todos, insertAt, getSelectedIndices().length, structured)
        : pasteInlineSplit(todos, index, getCursor(e), structured);
      if (hasBlockSelection) clearBlockSelection();
      setTodos(newTodos);
      requestAnimationFrame(() => {
        const focusIdx = hasBlockSelection
          ? insertAt + structured.length - 1
          : index + structured.length;
        inputRefs.current[focusIdx]?.focus();
      });
      return;
    }
    if (hasBlockSelection) {
      e.preventDefault();
      const selected = getSelectedIndices();
      if (selected.length > 0) {
        const start = selected[0];
        const prepared = [...todos];
        prepared.splice(start, selected.length, {
          id: uuid(),
          value: "",
          showBullet: false,
          mentionUserIds: [],
          isChecked: false,
        });
        const result = checkPastedBoard(text, start, 0, prepared);
        setTodos(result);
        clearBlockSelection();
        requestAnimationFrame(() => {
          inputRefs.current[start]?.focus();
        });
        return;
      }
    }
    e.preventDefault();
    const result = checkPastedBoard(text, index, getCursor(e));
    setTodos(result);
  };

  // textarea 높이 계산 (내용 감소 시에도 축소)
  const resizeTextareaEl = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const calcTextAreaHeight = (e) => {
    resizeTextareaEl(e.target);
  };

  useEffect(() => {
    // todos 변경 시 모든 textarea 높이 재계산
    requestAnimationFrame(() => {
      (inputRefs.current || []).forEach((el) => resizeTextareaEl(el));
    });
  }, [todos]);

  // 드래그 중에만 배경 차단 (textarea 바깥만)
  useEffect(() => {
    const onSelectStart = (ev) => {
      if (!isMouseSelectingRef.current) return;
      const inTextarea = isTargetTextarea(ev.target);
      if (!inTextarea) ev.preventDefault();
    };
    document.addEventListener("selectstart", onSelectStart, { capture: true });
    return () => document.removeEventListener("selectstart", onSelectStart, { capture: true });
  }, []);

  // 드래그 중에는 memoContent 내부 배경을 투명 처리
  useEffect(() => {
    let styleEl;
    if (hideNativeSelection) {
      styleEl = document.createElement("style");
      styleEl.setAttribute("data-custom-selection-hide", "true");
      styleEl.textContent =
        ".memoContent *::selection{background:transparent !important;color:inherit !important;}";
      document.head.appendChild(styleEl);
    }
    return () => {
      if (styleEl && styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
    };
  }, [hideNativeSelection]);

  // 멘션 리스트 위치 계산
  const calculateMentionPosition = (index) => {
    if (!inputRefs.current[index] || !memoRef.current) return { top: 0, left: 0 };

    const inputRect = inputRefs.current[index].getBoundingClientRect();
    const memoRect = memoRef.current.getBoundingClientRect();

    return {
      top: `${inputRect.bottom - memoRect.top}px`,
      left: `${inputRect.left - memoRect.left}px`,
    };
  };

  // 투두에서 선택된 멘션 사용자 제거
  const deleteMention = (id, mberNo) => {
    const updatedTodos = todos.map((todo) => {
      if (todo.id === id) {
        const filteredMentions = todo.mentionUserIds.filter((item) => {
          return item !== mberNo;
        });

        return { ...todo, mentionUserIds: filteredMentions };
      }
      return todo;
    });

    setTodos(updatedTodos);
  };

  // 멤버 번호로 사용자 이름을 조회
  const memberNameMatch = (mberNo) => {
    if (formData?.workerList?.[mberNo]) return formData.workerList[mberNo];
    if (formData.managerNo.toString() === mberNo.toString()) return formData.managerNm;

    return "";
  };

  // textarea 포커스 진입 시 상태 갱신
  const handleFocus = (index) => {
    setFocusIndex(index);
    setActiveMemo(true);
    // 드래그 중 포커스 변경 시
    if (!suppressClearSelectionRef.current && !isMouseSelectingRef.current) {
      // 포커스로 인한 불필요한 초기화 방지
      clearBlockSelection();
    }
  };

  // textarea 포커스 이탈 시 상태 갱신
  const handleBlur = () => {
    setActiveMemo(false);
  };

  // 빈 영역 클릭 시 마지막 행 끝으로 포커스 이동
  const clickMemoContent = (e) => {
    // 드래그 중 컨테이너 클릭 차단
    if (isMouseSelectingRef.current) {
      e.preventDefault();
      return;
    }
    const isTextarea = isTargetTextarea(e.target);
    if (isTextarea) return;

    const lastIndex = Math.max(0, todos.length - 1);
    const input = inputRefs.current[lastIndex];
    if (input) {
      input.focus();
      const len = input.value ? input.value.length : 0;
      requestAnimationFrame(() => {
        input.setSelectionRange(len, len);
      });
    }
  };

  return (
    <div
      className={`memoContent ${activeMemo ? "active" : ""}`}
      ref={memoRef}
      style={{ touchAction: "none" }}
      onCopy={handleCopy}
      onCut={handleCut}
      onClick={clickMemoContent}
      onPointerUp={handleMouseUpAnywhere}
      // onPointerLeave={handleMouseLeave}
      onPointerMove={handleMouseMove}
      onPointerCancel={handleMouseUpAnywhere}>
      {todos.map((todo, index) => (
        <div
          key={todo.id}
          data-row-index={index}
          style={{
            background:
              (hasBlockSelection && getSelectedIndices().includes(index)) ||
              (hasCharSelection && isRowInCharSelection(index))
                ? "rgba(0,0,0,0.04)"
                : "transparent",
          }}
          onPointerDown={(e) => handleRowMouseDown(index, e)}
          onPointerEnter={(e) => handleRowMouseEnter(index, e)}>
          {hasCharSelection &&
            getRowSelectionOffsets(index) &&
            (() => {
              const text = todo.value || "";
              const sel = getRowSelectionOffsets(index);
              const before = text.slice(0, sel.startOffset);
              const selected = text.slice(sel.startOffset, sel.endOffset);
              const after = text.slice(sel.endOffset);
              return (
                <div
                  className="row-selection-overlay"
                  style={{
                    position: "absolute",
                    left: todo.showBullet ? 30 : 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    pointerEvents: "none",
                    overflow: "hidden",
                    zIndex: 2,
                  }}>
                  <div style={getOverlayInnerStyle(index)}>
                    <span>{before}</span>
                    <span style={{ background: "rgba(56, 132, 255, 0.25)" }}>{selected}</span>
                    <span>{after}</span>
                  </div>
                </div>
              );
            })()}
          {todo.showBullet && (
            <div>
              <CustomInput className="block">
                <input type="checkbox" disabled checked={todo.isChecked} />
                <span className="checkmark"></span>
              </CustomInput>
            </div>
          )}
          <textarea
            style={{
              width: todo.showBullet ? "calc(100% - 30px)" : "100%",
              backgroundColor: hasCharSelection ? "transparent" : undefined,
              WebkitTapHighlightColor: "transparent",
              WebkitTextFillColor: undefined,
              position: "relative",
              zIndex: 1,
            }}
            rows="1"
            type="text"
            ref={(el) => (inputRefs.current[index] = el)}
            value={todo.value}
            onPointerDown={(e) => handleRowMouseDown(index, e)}
            onPointerEnter={(e) => handleRowMouseEnter(index, e)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onChange={(e) => handleChange(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            onFocus={() => handleFocus(index)}
            onBlur={() => handleBlur()}
            placeholder={focusIndex === index ? "메모" : ""}
            className={todo.showBullet ? "todo" : "text"}
            onInput={(e) => calcTextAreaHeight(e)}
            onCompositionStart={startComposition}
            onSelect={(e) => {
              // 마우스 버튼이 눌린 상태에서만 커스텀 선택 반영
              if (!isMouseSelectingRef.current && !e.shiftKey) return;
              const start = Math.min(e.target.selectionStart || 0, e.target.selectionEnd || 0);
              const end = Math.max(e.target.selectionStart || 0, e.target.selectionEnd || 0);
              if (start === end) return;
              if (!charSelAnchor) setCharSelAnchor({ row: index, offset: start });
              setCharSelFocus({ row: index, offset: end });
            }}
          />
          {todo.mentionUserIds && todo.mentionUserIds.length > 0 && (
            <ul className="select-mention" style={{ marginLeft: todo.showBullet ? 30 : 0 }}>
              {todo.mentionUserIds.map((item) => (
                <li key={item}>
                  <span>{memberNameMatch(item)}</span>
                  <button
                    className="del"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteMention(todo.id, item);
                    }}>
                    <MdClose />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
      {isMentionOpen &&
        formData.gbn !== "P" &&
        matchingMentions.length > 0 &&
        focusIndex !== null &&
        todos[focusIndex]?.showBullet && (
          <ul
            className="mentions"
            style={{
              ...calculateMentionPosition(focusIndex),
            }}
            ref={mberRef}>
            {matchingMentions.map((mention, idx) => (
              <li
                key={mention.mberNo}
                className={idx === (mentionHoverIndex ?? mentionActiveIndex) ? "active" : ""}
                onMouseEnter={() => setMentionHoverIndex(idx)}
                onMouseLeave={() => setMentionHoverIndex(null)}
                onClick={() => handleSelectMention(mention.mberNo)}>
                {mention.mberNm}
              </li>
            ))}
          </ul>
        )}
    </div>
  );
};

export default TodoScreen;
